const CHUNK_LOAD_RECOVERY_STORAGE_KEY = 'synmatch.chunkLoadRecoveryAttempted';

const MAX_STORED_RECOVERY_MARKERS = 8;

const CHUNK_LOAD_ERROR_MESSAGE_PATTERN = /Loading chunk \d+ failed/i;

const CHUNK_LOAD_ERROR_REQUEST_PATTERN =
  /\((?:missing|timeout|error): ([^)]+)\)/i;

const getChunkLoadRecoveryMarker = (error: Error): string => {
  const request = (error as Error & { request?: unknown }).request;

  if (typeof request === 'string' && request.length > 0) {
    return request;
  }

  return (
    CHUNK_LOAD_ERROR_REQUEST_PATTERN.exec(error.message)?.[1] ?? error.message
  );
};

const parseStoredRecoveryMarkers = (rawMarkers: string | null): string[] => {
  if (rawMarkers === null) {
    return [];
  }

  const trimmedRawMarkers = rawMarkers.trim();

  if (
    !trimmedRawMarkers.startsWith('[') &&
    !trimmedRawMarkers.startsWith('{')
  ) {
    return [rawMarkers];
  }

  try {
    const parsedMarkers: unknown = JSON.parse(rawMarkers);

    if (!Array.isArray(parsedMarkers)) {
      return [];
    }

    return parsedMarkers.filter(
      (storedMarker): storedMarker is string => typeof storedMarker === 'string'
    );
  } catch (error) {
    if (error instanceof SyntaxError) {
      return [];
    }

    throw error;
  }
};

const isRecoverableStorageError = (error: unknown): boolean =>
  error instanceof DOMException || error instanceof TypeError;

const readRecoveryAttemptedMarkers = (): Set<string> | null => {
  try {
    return new Set(
      parseStoredRecoveryMarkers(
        globalThis.sessionStorage.getItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY)
      )
    );
  } catch (error) {
    if (isRecoverableStorageError(error)) {
      return null;
    }

    throw error;
  }
};

const writeRecoveryAttemptedMarkers = (markers: Set<string>): boolean => {
  const storedMarkers = Array.from(markers).slice(-MAX_STORED_RECOVERY_MARKERS);

  try {
    globalThis.sessionStorage.setItem(
      CHUNK_LOAD_RECOVERY_STORAGE_KEY,
      JSON.stringify(storedMarkers)
    );

    return true;
  } catch (error) {
    if (isRecoverableStorageError(error)) {
      return false;
    }

    throw error;
  }
};

export const isChunkLoadError = (error: Error): boolean =>
  error.name === 'ChunkLoadError' ||
  CHUNK_LOAD_ERROR_MESSAGE_PATTERN.test(error.message);

export const reloadOnceForChunkLoadError = (error: Error): boolean => {
  if (!isChunkLoadError(error)) {
    return false;
  }

  const marker = getChunkLoadRecoveryMarker(error);
  const attemptedMarkers = readRecoveryAttemptedMarkers();

  if (attemptedMarkers === null || attemptedMarkers.has(marker)) {
    return false;
  }

  attemptedMarkers.add(marker);

  if (!writeRecoveryAttemptedMarkers(attemptedMarkers)) {
    return false;
  }

  globalThis.location.reload();

  return true;
};
